import { RiskFactor, RiskLevel, RiskScoreInputs } from "./RiskScore";

const SENSITIVE_SECTORS = new Set(["REAL_ESTATE", "FOREIGN_EXCHANGE", "GAMBLING"]);

// Illustrative stand-in for the FATF/GAFI high-risk & non-cooperative
// jurisdictions list — the real list is maintained externally and updated
// periodically; no such referential exists in this codebase yet.
const GAFI_NON_COOPERATIVE_COUNTRIES = new Set(["IR", "KP", "MM"]);

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function getRiskLevel(compositeScore: number): RiskLevel {
  if (compositeScore > 85) {
    return "CRITICAL";
  }
  if (compositeScore > 60) {
    return "HIGH";
  }
  if (compositeScore > 30) {
    return "STANDARD";
  }
  return "LOW";
}

/**
 * Deterministic stand-in for a real BCEAO composite-scoring endpoint — no
 * endpoint exists yet, and no dedicated backend user story was identified
 * under EPIC-KYB-07 for composite scoring itself (see #25468; clarify with
 * the backend owner before wiring this to a real contract). Weighted factors
 * and LOW/STANDARD/HIGH/CRITICAL bands mirror the prototype
 * (`docs/kyb-pm.html`, étape « Score de risque »).
 */
export function generateMockRiskScore(inputs: RiskScoreInputs): {
  compositeScore: number;
  level: RiskLevel;
  factors: RiskFactor[];
} {
  const factors: RiskFactor[] = [{ id: "BASE", weight: 20 }];

  if (inputs.hasComplexHoldingStructure) {
    factors.push({ id: "HOLDING_STRUCTURE", weight: 15 });
  }
  if (inputs.hasIntermediatePM) {
    factors.push({ id: "INTERMEDIATE_PM", weight: 15 });
  }
  if (inputs.hasForeignUbo) {
    factors.push({ id: "FOREIGN_UBO", weight: 10 });
  }
  if (SENSITIVE_SECTORS.has(inputs.activitySector)) {
    factors.push({ id: "SENSITIVE_SECTOR", weight: 25 });
  }
  if (inputs.hasPepRepresentative) {
    factors.push({ id: "PEP_REPRESENTATIVE", weight: 20 });
  }
  if (inputs.isDigitalChannel) {
    factors.push({ id: "DIGITAL_CHANNEL", weight: 5 });
  }
  if (GAFI_NON_COOPERATIVE_COUNTRIES.has(inputs.incorporationCountry)) {
    factors.push({ id: "GAFI_NON_COOPERATIVE_COUNTRY", weight: 30 });
  }
  if (inputs.isYoungCompany) {
    factors.push({ id: "YOUNG_COMPANY", weight: 5 });
  }

  const compositeScore = Math.min(
    100,
    factors.reduce((sum, factor) => sum + factor.weight, 0),
  );

  return { compositeScore, level: getRiskLevel(compositeScore), factors };
}
